import React from 'react';
import { Helmet } from 'react-helmet-async';


const whatDeleted = [
  { icon: '👤', title: 'Your Profile', desc: 'Your name, photos, bio, and all profile information will be permanently removed from the Platform.' },
  { icon: '🔗', title: 'Connections & Interests', desc: 'Any interests sent or received, saved profiles, and match history will be erased completely.' },
  { icon: '🔔', title: 'Notifications & Preferences', desc: 'Your notification settings, saved searches, and account preferences will all be removed.' },
  { icon: '💳', title: 'Subscription Access', desc: 'Any unused premium credits or purchased services will be forfeited upon account deletion and cannot be refunded or transferred.' },
];


export default function DeleteAccount() {
  return (
    <div style={{ paddingTop: '70px' }}>
      <Helmet>
        <title>Delete Account - Weddyfyp Matrimony</title>
        <meta name="description" content="Learn how to permanently delete your Weddyfyp account and what data gets removed. Follow the simple steps to complete your account deletion request." />
        <link rel="canonical" href="https://weddyfyp.com/delete-account" />
      </Helmet>

      {/* ── Hero ── */}
      <section style={{ background: 'linear-gradient(150deg, #fdf5f8, #fdf5f8)', padding: '70px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <div style={{ fontSize: '50px', marginBottom: '18px' }}>🗑️</div>
          <p style={{ fontSize: '12px', color: '#C2185B', fontWeight: '700', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '14px' }}>ACCOUNT DELETION</p>
          <h1 style={{ fontSize: 'clamp(28px, 5vw, 44px)', color: '#1c1917', marginBottom: '16px', lineHeight: '1.2' }}>
            How to Delete Your Weddyfyp Account
          </h1>
          <p style={{ fontSize: '17px', color: '#6b7280', lineHeight: '1.8' }}>
            We respect your right to leave. If you have decided to close your account, this page explains exactly what will happen, what data will be removed, and how to send us your request.
          </p>
        </div>
      </section>

      {/* ── Steps ── */}
      <section style={{ padding: '72px 24px', background: '#fff' }}>
        <div style={{ maxWidth: '680px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '44px' }}>
            <p style={{ fontSize: '12px', color: '#C2185B', fontWeight: '700', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '14px' }}>HOW TO DELETE YOUR ACCOUNT</p>
            <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', color: '#1c1917', marginBottom: '14px' }}>Follow These Steps in the App</h2>
            <p style={{ fontSize: '16px', color: '#6b7280', lineHeight: '1.75' }}>
              Account deletion is done directly from within the Weddyfyp app. It takes less than a minute.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {[
              { step: '1', title: 'Open the app', desc: 'Launch the Weddyfyp app on your mobile device and log in to your account.' },
              { step: '2', title: 'Go to Settings section', desc: 'Tap the Settings icon from your profile or the main menu to open account settings.' },
              { step: '3', title: 'Tap on Delete Account', desc: 'Scroll to find the "Delete Account" option and tap it to begin the deletion process.' },
              { step: '4', title: 'Confirm your choice', desc: 'Review the information shown and confirm that you wish to permanently delete your account.' },
            ].map((s, i) => (
              <div key={i} style={{
                display: 'flex', gap: '20px', alignItems: 'flex-start',
                background: '#fff', borderRadius: '18px', padding: '24px 26px',
                border: '1.5px solid #f3f4f6',
                boxShadow: '0 2px 12px rgba(194,24,91,0.04)',
                transition: 'border-color 0.2s, box-shadow 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#f8bbd0'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(194,24,91,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#f3f4f6'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(194,24,91,0.04)'; }}
              >
                <div style={{
                  width: '48px', height: '48px', borderRadius: '50%', flexShrink: 0,
                  background: 'linear-gradient(135deg, #C2185B, #AD1457)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 6px 18px rgba(194,24,91,0.28)',
                }}>
                  <span style={{ color: '#fff', fontWeight: '800', fontSize: '18px', fontFamily: "'Playfair Display', serif" }}>{s.step}</span>
                </div>
                <div style={{ paddingTop: '4px' }}>
                  <h3 style={{ fontSize: '17px', color: '#1f2937', fontWeight: '700', marginBottom: '6px' }}>{s.title}</h3>
                  <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.7' }}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '28px', padding: '18px 22px', background: '#fce4ec', borderRadius: '14px', border: '1px solid #f8bbd0', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '18px', flexShrink: 0 }}>⚠️</span>
            <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.65' }}>
              <strong>This action is permanent.</strong> Once you confirm deletion inside the app, your account and all associated data cannot be recovered.
            </p>
          </div>
        </div>
      </section>

      {/* ── What Gets Deleted ── */}
      <section style={{ padding: '72px 24px', background: '#faf8f7' }}>
        <div style={{ maxWidth: '820px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '44px' }}>
            <p style={{ fontSize: '12px', color: '#C2185B', fontWeight: '700', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '14px' }}>WHAT WILL BE REMOVED</p>
            <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', color: '#1c1917', marginBottom: '14px' }}>Everything Associated with Your Account</h2>
            <p style={{ fontSize: '15px', color: '#6b7280', lineHeight: '1.75' }}>
              Deletion is permanent and irreversible. Once completed, none of the following can be recovered.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
            {whatDeleted.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: '16px', padding: '22px', borderRadius: '16px', background: '#fff', border: '1px solid #f3f4f6' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#fce4ec', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>
                  {item.icon}
                </div>
                <div>
                  <h4 style={{ fontSize: '15px', color: '#1f2937', fontWeight: '700', marginBottom: '5px' }}>{item.title}</h4>
                  <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.65' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '24px', padding: '18px 22px', background: '#fce4ec', borderRadius: '14px', border: '1px solid #f8bbd0', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '18px', flexShrink: 0 }}>⚠️</span>
            <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.65' }}>
              <strong>Note:</strong> Certain records such as payment transactions and abuse-related reports may be retained for up to 7 years to comply with financial and legal regulations. This is required under Indian law and cannot be waived.
            </p>
          </div>
        </div>
      </section>

      {/* ── Note ── */}
    {/*  <section style={{ padding: '0 24px 80px', background: '#fff' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '16px', padding: '24px 28px' }}>
            <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '22px', flexShrink: 0 }}>💡</span>
              <div>
                <h3 style={{ fontSize: '16px', color: '#15803d', fontWeight: '700', marginBottom: '8px' }}>Before You Delete — Consider Pausing Instead</h3>
                <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.72' }}>
                  If you simply need a break or want to hide your profile temporarily, you can pause your account. Your data is preserved and you can return anytime without starting over.
                  To pause, go to Settings in the app, or mention it in your email to us and we will help you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section> */}

    </div>
  );
}
